<template>
    <b-modal v-model="visible" title="View SMS Log Audit" @hidden="handleModalHidden" size="lg" scrollable
        hide-footer>
        <template v-if="smsLog && questionSet">
            <div class="mb-3">
                <h5>Conversation Information</h5>
                <div class="row">
                    <div class="col-md-6">
                        <strong>External Number:</strong> {{ smsLog.externalNumber }}
                    </div>
                    <div class="col-md-6">
                        <strong>Agent:</strong> {{ smsLog.agent }}
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-6">
                        <strong>Started:</strong> {{ formatDate(smsLog.initiated) }}
                    </div>
                    <div class="col-md-6">
                        <strong>Messages:</strong> {{ smsLog.messageCount }}
                    </div>
                </div>
                <div v-if="smsLog.patientFirstName || smsLog.patientLastName" class="row">
                    <div class="col-md-12">
                        <strong>Patient:</strong> {{ smsLog.patientFirstName }} {{ smsLog.patientLastName }}
                    </div>
                </div>
            </div>

            <hr />

            <div class="mb-3">
                <h5>Audit Form: {{ questionSet.name }}</h5>
                <div v-if="smsLog.auditCompletedBy || smsLog.auditCompletedAt" class="text-muted">
                    <small>
                        <strong>Audit Details:</strong>
                        <span v-if="smsLog.auditCompletedBy">
                            Completed by {{ smsLog.auditCompletedBy }}
                        </span>
                        <span v-if="smsLog.auditCompletedAt">
                            on {{ formatDate(smsLog.auditCompletedAt) }}
                        </span>
                    </small>
                </div>
            </div>

            <!-- Questions in read-only mode -->
            <div>
                <TaskQuestions :questions="questionSet.questions" :answers="filteredAnswers"
                    :disabled="true" :editing="false" :entity-type="EntityType.SMSLog" />
            </div>

            <div class="text-center mt-3">
                <b-button variant="secondary" @click="visible = false">Close</b-button>
            </div>
        </template>

        <template v-else-if="loading">
            <div class="text-center">
                <b-spinner />
                <p class="mt-2">Loading audit information...</p>
            </div>
        </template>

        <template v-else>
            <div class="text-center text-muted">
                <p>No audit data available for this conversation.</p>
                <b-button variant="secondary" @click="visible = false">Close</b-button>
            </div>
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import moment from 'moment';
import { SmsLog } from '@/models/SmsLog';
import { UserTaskQuestionSet, UserTaskAnswer } from '@/models/UserTask';
import { EntityType } from '@/models/EntityType';
import TaskQuestions from '@/components/Tasks/TaskQuestions.vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

@Component({
    name: "SmsLogAuditViewer",
    components: {
        TaskQuestions
    }
})
export default class SmsLogAuditViewer extends Vue {
    EntityType = EntityType;

    @Prop({ required: true }) value!: boolean;
    @Prop({ required: true }) smsLog!: SmsLog | null;

    private questionSet: UserTaskQuestionSet | null = null;
    private auditAnswers: UserTaskAnswer[] = [];
    private loading = false;

    get visible(): boolean {
        return this.value;
    }

    set visible(val: boolean) {
        this.$emit('input', val);
    }

    get filteredAnswers(): UserTaskAnswer[] {
        return this.auditAnswers;
    }

    @Watch('visible')
    async onVisibleChange(newVal: boolean) {
        if (newVal && this.smsLog) {
            await this.loadAuditData();
        }
        if (!newVal) {
            this.resetData();
        }
    }

    async loadAuditData() {
        if (!this.smsLog?.id) return;

        this.loading = true;
        try {
            const answersResponse = await this.$http.get<UserTaskAnswer[]>(`/SmsLog/audit-answers/${this.smsLog.id}`);
            this.auditAnswers = answersResponse.data;

            if (this.auditAnswers.length === 0) {
                return;
            }

            if (this.smsLog.auditQuestionSetId) {
                await this.loadQuestionSetById(this.smsLog.auditQuestionSetId);
            } else {
                // Fall back to searching if no metadata found (legacy audits)
                const firstAnswer = this.auditAnswers.find(a => a.taskQuestionId > 0);
                if (firstAnswer) {
                    await this.findQuestionSetForAnswer(firstAnswer.taskQuestionId);
                }
            }
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error('Failed to load audit data:', err);
        } finally {
            this.loading = false;
        }
    }

    async loadQuestionSetById(questionSetId: number) {
        try {
            const response = await this.$http.get<UserTaskQuestionSet>(`/task/question-set/${questionSetId}`);
            this.questionSet = response.data;
        } catch (err) {
            console.error(`Failed to load question set ${questionSetId}:`, err);
            const firstAnswer = this.auditAnswers.find(a => a.taskQuestionId > 0);
            if (firstAnswer) {
                await this.findQuestionSetForAnswer(firstAnswer.taskQuestionId);
            }
        }
    }

    async findQuestionSetForAnswer(questionId: number) {
        try {
            const questionSetsResponse = await this.$http.get<UserTaskQuestionSet[]>(`/task/question-sets/${EntityType.SMSLog}`);
            const questionSets = questionSetsResponse.data;

            for (const qs of questionSets) {
                try {
                    const fullQuestionSet = await this.$http.get<UserTaskQuestionSet>(`/task/question-set/${qs.id}`);
                    if (fullQuestionSet.data.questions?.some(q => q.id === questionId)) {
                        this.questionSet = fullQuestionSet.data;
                        return;
                    }
                } catch (err) {
                    console.warn(`Failed to load question set ${qs.id}:`, err);
                    continue;
                }
            }

            console.warn(`Question set containing question ${questionId} not found`);
        } catch (err) {
            console.error('Failed to find question set:', err);
        }
    }

    resetData() {
        this.questionSet = null;
        this.auditAnswers = [];
        this.loading = false;
    }

    formatDate(date: Date | string | undefined): string {
        if (!date) return '';
        return moment.utc(date).local().format("L LT");
    }

    handleModalHidden() {
        this.resetData();
    }
}
</script>

<style scoped>
.sms-info {
    background-color: #f8f9fa;
    padding: 1rem;
    border-radius: 0.25rem;
    margin-bottom: 1rem;
}
</style>
